import {
  CarOption,
  FlightOption,
  HotelOption,
  UpdateSegmentRequest,
} from '../../../models/itinerary';
import { ActionTypes } from '../../constants/action-types';
import { ItineraryReceiveAction } from '../itinerary/types';
import { SegmentResponse } from './../../../models/itinerary';
import {
  SelectedOptionsReducerActions,
  SelectOptionWithRateAction,
} from './types';

export interface SelectedOptionsReducerState {
  [segmentId: string]: UpdateSegmentRequest;
}

export const selectedOptionsInitialState: SelectedOptionsReducerState = {};

type SegmentResponseWithAirSegment = SegmentResponse & {
  airSegment: Required<SegmentResponse>['airSegment'];
};
type SegmentResponseWithCarSegment = SegmentResponse & {
  carSegment: Required<SegmentResponse>['carSegment'];
};
type SegmentResponseWithHotelSegment = SegmentResponse & {
  hotelSegment: Required<SegmentResponse>['hotelSegment'];
};

const formatAirSegment = (
  segment: SegmentResponseWithAirSegment,
): FlightOption => ({
  flightId: segment.airSegment.recommended.flightId,
  fareId: segment.airSegment.recommended.fareOptions.find(
    fareOption => fareOption.recommended,
  )!.fareId,
});

const formatCarSegment = (
  segment: SegmentResponseWithCarSegment,
): CarOption => ({
  supplierCode: segment.carSegment.recommended.supplierCode,
  carId: segment.carSegment.recommended.carTypes.find(
    carType => carType.recommended,
  )!.carId,
});

const formatHotelSegment = (
  segment: SegmentResponseWithHotelSegment,
): HotelOption => ({
  hotelId: segment.hotelSegment.recommended.hotelId,
  rateId: segment.hotelSegment.recommended.rates.find(
    rateOption => rateOption.recommended,
  )!.rateId,
});

const getSegmentType = (segment: SegmentResponse) => {
  if (segment.airSegment) {
    return 'Air';
  }
  if (segment.carSegment) {
    return 'Car';
  }
  if (segment.hotelSegment) {
    return 'Hotel';
  }
};

export function SelectedOptionsReducer(
  state = selectedOptionsInitialState,
  action: SelectedOptionsReducerActions,
): SelectedOptionsReducerState {
  switch (action.type) {
    // Map the incoming itinerary data to setup default options
    case ActionTypes.ITINERARY_GET_RECEIVE:
      const itinerary = (action as ItineraryReceiveAction).data.itinerary;
      return itinerary.reduce((previousValue, segment) => {
        return {
          ...previousValue,
          [segment.orderId]: {
            index: segment.orderId,
            segmentType: getSegmentType(segment),
            airSegment: !!segment.airSegment
              ? formatAirSegment(segment as SegmentResponseWithAirSegment)
              : undefined,
            hotelSegment: !!segment.hotelSegment
              ? formatHotelSegment(segment as SegmentResponseWithHotelSegment)
              : undefined,
            carSegment: !!segment.carSegment
              ? formatCarSegment(segment as SegmentResponseWithCarSegment)
              : undefined,
            recommendedSelected: true,
          },
        };
      }, {});

    case ActionTypes.OPTION_SELECT_WITH_RATE:
      const {
        segmentId,
        option,
        isRecommended,
      } = action as SelectOptionWithRateAction;
      const type = state[segmentId].segmentType;

      const airSegment =
        type === 'Air' ? (option as FlightOption) : state[segmentId].airSegment;

      const hotelSegment =
        type === 'Hotel'
          ? (option as HotelOption)
          : state[segmentId].hotelSegment;

      const carSegment =
        type === 'Car' ? (option as CarOption) : state[segmentId].carSegment;

      return {
        ...state,
        [segmentId]: {
          ...state[segmentId],
          airSegment,
          hotelSegment,
          carSegment,
          recommendedSelected: isRecommended,
        },
      };

    default:
      return state;
  }
}
